/**
 * W5 — Audit-log + per-user stats API client.
 *
 * Thin fetch wrapper matching the `stats-api.ts` conventions (RTK Query is
 * intentionally NOT used here for consistency with the rest of the Phase 8 /
 * agency surface area). All requests use the agency Bearer token sourced from
 * Redux (`state.auth.agency.accessToken`).
 *
 * Backend contract (PR #2020):
 *   GET /api/v1/agency/stats/per-user?window=7d|30d|90d
 *     → { window, window_start, window_end, users: [...] }
 *   GET /api/v1/agency/audit-log?shop=&employee=&action=&from=&to=&page=&per_page=
 *     → { data: [...], meta: { total, per_page, current_page, last_page } }
 *
 * Both endpoints are owner/team_lead only (the API 403s other roles).
 *
 * Responses are unwrapped defensively: the backend may return the payload bare
 * OR wrapped under a top-level `{ data }` envelope. `unwrap` handles both.
 */
import { CENTRAL_API_BASE_URL } from '../../constants/central-api';
import store from '../../../store/store';
import type { StatsWindow } from '../stats/stats-api';

// ---------- shared types ----------

export type { StatsWindow } from '../stats/stats-api';

/** A single employee row in the per-user stats breakdown. */
export interface PerUserStatsRow {
  employee_id: string;
  name: string;
  email: string;
  role: string;
  proposed_count: number;
  approved_count: number;
  denied_count: number;
  /** 0.0 – 1.0 */
  approval_rate: number;
  /** ISO8601, or null when the employee has never been active. */
  last_active_at: string | null;
}

export interface PerUserStatsResponse {
  window: StatsWindow;
  window_start: string; // ISO8601
  window_end: string; // ISO8601
  users: PerUserStatsRow[];
}

/** A single audit-log entry. */
export interface AuditLogEntry {
  id: string | number;
  created_at: string; // ISO8601
  /** Raw actor token, e.g. `user:agency:{id}` or `system`. */
  actor: string;
  action: string;
  shop_slug: string | null;
  shop_name: string | null;
  proposal_id: string | number | null;
  platform: string | null;
  // The API sends this as a string for some events and a small object
  // (e.g. `{ notes }`, `{ reason }`, `{ error, result }`) for others — the UI
  // renders it via `formatDetail()` which handles both. Rendering a raw object
  // as a React child throws (React #31).
  detail: string | Record<string, unknown> | null;
  risk_level: string | null;
}

export interface AuditLogMeta {
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
}

export interface AuditLogResponse {
  data: AuditLogEntry[];
  meta: AuditLogMeta;
}

/** Filters accepted by `listAuditLog`. All fields optional. */
export interface AuditLogFilters {
  shop?: string;
  employee?: string;
  action?: string;
  /** ISO date (YYYY-MM-DD) or ISO8601. */
  from?: string;
  /** ISO date (YYYY-MM-DD) or ISO8601. */
  to?: string;
  page?: number;
  per_page?: number;
}

// ---------- internal helpers ----------

function buildHeaders(token: string | undefined): Record<string, string> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

async function parseJson<T>(response: Response): Promise<T> {
  const text = await response.text();
  if (!text) {
    return {} as T;
  }
  try {
    return JSON.parse(text) as T;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[audit-api] failed to parse JSON', err, text);
    throw new Error('Invalid response from server.');
  }
}

async function request<T>(
  path: string,
  token: string | undefined,
  init: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    ...buildHeaders(token),
    ...((init.headers as Record<string, string>) || {}),
  };
  if (init.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${CENTRAL_API_BASE_URL}${path}`, {
    ...init,
    headers,
  });

  if (!response.ok) {
    let detail = '';
    try {
      const body = await parseJson<{ message?: string; error?: string }>(
        response
      );
      detail = body?.message || body?.error || '';
    } catch {
      // swallow — fall back to status text
    }
    const err = new Error(
      detail || `Request failed (${response.status} ${response.statusText})`
    ) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }

  return parseJson<T>(response);
}

/**
 * Unwrap a possibly-`{data}`-wrapped payload. Used for the per-user endpoint
 * where the meaningful shape is the object itself (NOT `data`). The audit-log
 * endpoint intentionally keeps its `{ data, meta }` envelope, so it does NOT
 * use this helper — see `normaliseAuditLog`.
 */
function unwrap<T>(raw: T | { data?: T }): T {
  if (
    raw &&
    typeof raw === 'object' &&
    'data' in (raw as Record<string, unknown>) &&
    (raw as { data?: T }).data !== undefined &&
    // Per-user payload carries `users`; if `users` is present at the top level
    // we already have the unwrapped shape and must NOT descend into `data`.
    !('users' in (raw as Record<string, unknown>))
  ) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

function buildWindowQuery(window: StatsWindow): string {
  const params = new URLSearchParams();
  params.set('window', window);
  return params.toString();
}

function buildAuditQuery(filters: AuditLogFilters): string {
  const params = new URLSearchParams();
  if (filters.shop) params.set('shop', filters.shop);
  if (filters.employee) params.set('employee', filters.employee);
  if (filters.action) params.set('action', filters.action);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.page && filters.page > 0) {
    params.set('page', String(filters.page));
  }
  if (filters.per_page && filters.per_page > 0) {
    params.set('per_page', String(filters.per_page));
  }
  return params.toString();
}

/**
 * Normalise the audit-log response so callers always get `{ data, meta }`.
 *
 * Accepts:
 *   - the canonical `{ data: [...], meta: {...} }` envelope, or
 *   - a bare array of entries (defensive — backend variants), in which case we
 *     synthesise a single-page meta block.
 */
function normaliseAuditLog(
  raw: AuditLogResponse | AuditLogEntry[] | { data?: AuditLogEntry[] },
  requestedPage: number,
  requestedPerPage: number
): AuditLogResponse {
  // Bare array form.
  if (Array.isArray(raw)) {
    return {
      data: raw,
      meta: {
        total: raw.length,
        per_page: requestedPerPage,
        current_page: requestedPage,
        last_page: 1,
      },
    };
  }

  // Descend the ApiResponse envelope `{ success, message, data: <payload> }`.
  // central-api (ApiResponse::success) nests the list one level deeper than the
  // bare `{ data, meta }` this normaliser originally assumed. When `.data` is a
  // non-array object, the real list payload lives inside it; when `.data` is
  // already an array (or absent), the payload is top-level and we leave it
  // untouched — so bare and wrapped shapes both work and this can't regress.
  const obj = (raw ?? {}) as { data?: unknown; meta?: Partial<AuditLogMeta> };
  const descended: unknown =
    obj.data != null && typeof obj.data === 'object' && !Array.isArray(obj.data)
      ? obj.data
      : obj;

  // The descended payload may itself be a bare array.
  if (Array.isArray(descended)) {
    return {
      data: descended as AuditLogEntry[],
      meta: {
        total: descended.length,
        per_page: requestedPerPage,
        current_page: requestedPage,
        last_page: 1,
      },
    };
  }

  const inner = descended as { data?: unknown; meta?: Partial<AuditLogMeta> };
  const data = Array.isArray(inner.data) ? (inner.data as AuditLogEntry[]) : [];
  const meta: AuditLogMeta = {
    total: inner.meta?.total ?? data.length,
    per_page: inner.meta?.per_page ?? requestedPerPage,
    current_page: inner.meta?.current_page ?? requestedPage,
    last_page: inner.meta?.last_page ?? 1,
  };
  return { data, meta };
}

// ---------- public API ----------

/**
 * Fetch per-user proposal stats for the acting employee's organisation.
 * Backend enforces owner/team_lead role (403 otherwise).
 */
export async function getPerUserStats(
  window: StatsWindow,
  token?: string
): Promise<PerUserStatsResponse> {
  const t = token ?? store.getState().auth.agency.accessToken;
  const raw = await request<
    PerUserStatsResponse | { data?: PerUserStatsResponse }
  >(`agency/stats/per-user?${buildWindowQuery(window)}`, t, { method: 'GET' });
  const unwrapped = unwrap<PerUserStatsResponse>(raw);
  // Defensive: ensure `users` is always an array for the UI.
  return {
    ...unwrapped,
    users: Array.isArray(unwrapped?.users) ? unwrapped.users : [],
  };
}

/**
 * Fetch a paginated, filtered slice of the agency audit log.
 * Backend enforces owner/team_lead role (403 otherwise).
 */
export async function listAuditLog(
  filters: AuditLogFilters = {},
  token?: string
): Promise<AuditLogResponse> {
  const t = token ?? store.getState().auth.agency.accessToken;
  const requestedPage = filters.page && filters.page > 0 ? filters.page : 1;
  const requestedPerPage =
    filters.per_page && filters.per_page > 0 ? filters.per_page : 25;
  const qs = buildAuditQuery({
    ...filters,
    page: requestedPage,
    per_page: requestedPerPage,
  });
  const raw = await request<
    AuditLogResponse | AuditLogEntry[] | { data?: AuditLogEntry[] }
  >(`agency/audit-log?${qs}`, t, { method: 'GET' });
  return normaliseAuditLog(raw, requestedPage, requestedPerPage);
}

// ---------- team activity (owner only) ----------

/** The employee and team events the team activity log records. */
export type TeamAuditEvent =
  | 'employee.created_with_key'
  | 'employee.invited'
  | 'employee.key_regenerated'
  | 'employee.invitation_accepted'
  | 'employee.role_changed'
  | 'employee.reactivated'
  | 'employee.deactivated'
  | 'employee.updated'
  | 'employee.removed'
  | 'employee.invitation_resent'
  | 'team.created'
  | 'team.updated'
  | 'team.role_changed'
  | 'team.deleted'
  | 'team.member_added'
  | 'team.member_removed';

/** A single team activity entry. It never carries a key. */
export interface TeamAuditEntry {
  id: string | number;
  event: TeamAuditEvent | string;
  /** null when the change was made by the system. */
  actor: { id: string; name: string | null } | null;
  subject: { id: string; name: string | null } | null;
  properties: {
    subject_role?: string | null;
    old_role?: string | null;
    new_role?: string | null;
    changed_fields?: string[] | null;
    role?: string | null;
    /** Team membership entries: the team the member joined or left. */
    team_id?: string | null;
    team_name?: string | null;
  } | null;
  created_at: string; // ISO8601
}

export interface TeamAuditLogResponse {
  data: TeamAuditEntry[];
  meta: AuditLogMeta;
}

/**
 * Fetch one page of the team activity log (who added, invited, changed or
 * removed a teammate). The backend allows only the owner and answers 403 to
 * every other role.
 */
export async function listTeamAuditLog(
  params: { page?: number; per_page?: number } = {},
  token?: string
): Promise<TeamAuditLogResponse> {
  const t = token ?? store.getState().auth.agency.accessToken;
  const page = params.page && params.page > 0 ? params.page : 1;
  const perPage = Math.min(
    100,
    params.per_page && params.per_page > 0 ? params.per_page : 25
  );
  const qs = new URLSearchParams({
    page: String(page),
    per_page: String(perPage),
  }).toString();
  const raw = await request<{
    data?: TeamAuditEntry[];
    meta?: Partial<AuditLogMeta>;
  }>(`agency/audit-log/team?${qs}`, t, { method: 'GET' });
  const data = Array.isArray(raw?.data) ? raw.data : [];
  return {
    data,
    meta: {
      total: raw?.meta?.total ?? data.length,
      per_page: raw?.meta?.per_page ?? perPage,
      current_page: raw?.meta?.current_page ?? page,
      last_page: raw?.meta?.last_page ?? 1,
    },
  };
}
